import { useCallback, useState } from 'react';
import { SpinnerDot } from './ui';

/**
 * One way to report a save across the app: the stage transitions are driven by
 * the request itself, so a surface can't claim "Saved" before the server said
 * so. Pair `useSaveState()` with <SaveStatus /> next to the save button.
 */
export type SaveStage = 'idle' | 'saving' | 'saved' | 'error';

export interface SaveState {
  stage: SaveStage;
  /** True while the request is in flight — wire this to button `disabled`. */
  busy: boolean;
  error: string | null;
  /** When the last successful save completed. */
  savedAt: Date | null;
  /** Optional detail for the last success, e.g. "12 contracts imported". */
  note: string | null;
  /**
   * Runs `fn` and reports the stages. Resolves true when it saved, false when
   * it failed — the error is held in `error`, not thrown, so a caller can
   * simply `if (await save.run(...)) { … }`.
   */
  run: (fn: () => Promise<unknown>, note?: string) => Promise<boolean>;
  /**
   * Records a save that completed somewhere else (a modal that owns its own
   * request, say) so the surface behind it still reports when it finished.
   */
  markSaved: (note?: string) => void;
  /** Report a client-side validation failure through the same surface, so a
   *  rejected edit reads the same as a rejected request. */
  fail: (message: string) => void;
  /** Back to idle — call when the user starts editing again. */
  clear: () => void;
}

export function useSaveState(): SaveState {
  const [stage, setStage] = useState<SaveStage>('idle');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const run = useCallback(async (fn: () => Promise<unknown>, successNote?: string) => {
    setStage('saving');
    setError(null);
    setNote(null);
    try {
      await fn();
      setSavedAt(new Date());
      setNote(successNote ?? null);
      setStage('saved');
      return true;
    } catch (e) {
      setError((e as Error).message);
      setStage('error');
      return false;
    }
  }, []);

  const markSaved = useCallback((successNote?: string) => {
    setSavedAt(new Date());
    setNote(successNote ?? null);
    setError(null);
    setStage('saved');
  }, []);

  const clear = useCallback(() => {
    setStage('idle');
    setError(null);
    setNote(null);
  }, []);

  const fail = useCallback((message: string) => {
    setError(message);
    setStage('error');
  }, []);

  return { stage, busy: stage === 'saving', error, savedAt, note, run, markSaved, fail, clear };
}

const hhmmss = (d: Date) => d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/** The visible half: "Saving…" while in flight, then the time it completed. */
export function SaveStatus({ state, idleHint }: { state: SaveState; idleHint?: string }) {
  const base: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11 };

  if (state.stage === 'saving') {
    return <span style={{ ...base, color: 'var(--color-text-muted)' }} aria-live="polite"><SpinnerDot /> Saving…</span>;
  }
  if (state.stage === 'error') {
    return (
      <span style={{ ...base, color: 'var(--color-danger, #c0392b)' }} aria-live="polite" role="alert">
        Save failed — nothing was changed. {state.error}
      </span>
    );
  }
  if (state.stage === 'saved') {
    return (
      <span style={{ ...base, color: 'var(--color-green, #2e7d32)' }} aria-live="polite">
        ✓ Saved{state.savedAt ? ` ${hhmmss(state.savedAt)}` : ''}{state.note ? ` — ${state.note}` : ''}
      </span>
    );
  }
  return idleHint ? <span style={{ ...base, color: 'var(--color-text-muted)' }}>{idleHint}</span> : null;
}
